import type { RuleProblemDto } from "../api/types";

/**
 * Human-readable form of a rule problem, for badges, tooltips and the inbox
 * list. Says what is wrong and — for the case the user is most likely looking
 * at — what it means for the rule.
 *
 * Every problem is fatal: the backend refuses to match or apply a rule that has
 * one, because doing so would assign an entity the registry no longer permits.
 */
export const describeProblem = (problem: RuleProblemDto): string => {
  switch (problem.type) {
    case "hiddenEntity":
      return `uses the hidden ${problem.kind} “${problem.name}”`;
    case "missingEntity":
      return `uses the ${problem.kind} “${problem.name}”, which no longer exists`;
    case "conditionMatchesNoEntity":
      return `matches no ${problem.kind} named “${problem.value}”`;
    case "missingMatchSet":
      return "cites a match set that no longer exists";
    case "missingActionSet":
      return "cites an action set that no longer exists";
    case "missingActionSetInput":
      return `does not fill in “${problem.name}” for the action set it cites`;
    case "unknownTemplatePlaceholder":
      return problem.name.startsWith("$")
        ? `uses {${problem.name}}, which is not a transaction field`
        : `uses {${problem.name}}, which no action set input is named`;
    case "malformedTemplate":
      return `has unbalanced braces in “${problem.text}” — write {{ for a literal brace`;
    case "retiredField":
      return `targets the retired ${problem.field} field, which no longer exists`;
    case "unreadable":
      return `could not be read (${problem.error}) — delete it`;
  }
};

/**
 * Whether the rule is the stub the backend lists for a stored row it could
 * not parse. Nothing of the rule is known beyond its name, so it is not
 * editable — but unlike an invoice rule it *is* stored, and can be deleted.
 */
export const isUnreadable = (problems: RuleProblemDto[]): boolean =>
  problems.some((problem) => problem.type === "unreadable");

/** One line naming everything wrong with a rule. */
export const describeProblems = (problems: RuleProblemDto[]): string =>
  problems.map(describeProblem).join("; ");
